import type { ShareableRouteContext } from '@client/types/router';
import { isOverlayRoute } from '@client/utils/route-presentation';
import type { StaticDataRouteOption } from '@tanstack/react-router';
import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';

/**
 * Single source of truth for how route `staticData` (and `beforeLoad` context) resolve into the
 * page presentation: breadcrumb trail, page header title/subtitle, and page icon.
 *
 * These are pure functions over a structural view of TanStack Router matches so they can be
 * unit-tested with synthetic match arrays. `Breadcrumbs`, `PageLayoutHeaderLayout`, and the
 * icon hooks must all resolve through here — do not reimplement any of these fallback chains
 * in a component.
 */

/**
 * Structural subset of a TanStack `RouteMatch` used by the resolution functions.
 * Real matches from `useMatches()` are assignable to this.
 */
export type RouteMatchLike = {
    id: string;
    pathname: string;
    fullPath: string;
    staticData: StaticDataRouteOption;
    context?: ShareableRouteContext;
    params?: Record<string, unknown>;
    status?: string;
};

/** A breadcrumb/header title: static content, or an async resolver provided via route context. */
export type RouteTitle = ReactNode | (() => Promise<ReactNode>);

export type BreadcrumbTrailEntry = {
    breadcrumb: {
        title?: RouteTitle;
        inactive?: true;
    };
    pathname: string;
    fullPath: string;
    /**
     * The match is still loading, so its context is still its parent's: an async title on it
     * would resolve to the parent's title. Settled matches hold their own context.
     */
    pending?: true;
};

/**
 * Resolves the breadcrumb trail for a set of matches.
 *
 * Per match, the breadcrumb is (first hit wins):
 * 1. `staticData.breadcrumb`, its missing title backfilled from `staticData.metadata.title`
 *    (metadata backfill is skipped for overlay routes);
 * 2. `context.breadcrumb` from `beforeLoad` — deduplicated against the previous match, because
 *    context is inherited down the match chain and would otherwise repeat on every descendant;
 * 3. `staticData.metadata.title` alone (again, not for overlays).
 * Matches resolving to no breadcrumb are dropped.
 */
export function resolveBreadcrumbTrail(matches: readonly RouteMatchLike[]): BreadcrumbTrailEntry[] {
    return matches
        .map(({ context, staticData, pathname, fullPath, status }, i, array) => {
            const canUseMetadataForBreadcrumb = staticData.metadata && !isOverlayRoute(staticData);

            const breadcrumb: BreadcrumbTrailEntry['breadcrumb'] | undefined = staticData.breadcrumb
                ? {
                      ...staticData.breadcrumb,
                      title:
                          staticData.breadcrumb.title !== undefined
                              ? staticData.breadcrumb.title
                              : canUseMetadataForBreadcrumb
                                ? staticData.metadata?.title
                                : undefined,
                  }
                : context?.breadcrumb?.title && array[i - 1]?.context?.breadcrumb?.title !== context.breadcrumb.title
                  ? context.breadcrumb
                  : canUseMetadataForBreadcrumb
                    ? { title: staticData.metadata?.title }
                    : undefined;

            return { breadcrumb, pathname, fullPath, ...(status === 'pending' && { pending: true as const }) };
        })
        .filter((entry): entry is BreadcrumbTrailEntry => Boolean(entry.breadcrumb));
}

export type PageHeaderResolution = {
    title: RouteTitle | undefined;
    subtitle: string | undefined;
    /** The match the header describes: the deepest non-overlay match. */
    matchToUse: RouteMatchLike | undefined;
};

/**
 * Resolves the page header title/subtitle for a set of matches.
 *
 * The current route is the deepest non-overlay match. Title resolution (first truthy wins):
 * 1. `undefined` when the current route sets `ui.hideTitle`;
 * 2. the current route's `staticData.ui.title`;
 * 3. the current route's `context.ui.title` (async, from `beforeLoad`);
 * 4. an inherited title, when inheritance applies (explicit author intent beats the
 *    breadcrumb heuristic below);
 * 5. when the breadcrumb trail has exactly one entry: that entry's title, else the raw last
 *    match's `staticData.breadcrumb.title`.
 *
 * `ui.hideTitle` on a non-overlay ancestor also suppresses 4 and 5 for a current route without a
 * title of its own, so a record page's tab children stay untitled.
 *
 * Inheritance: `ui.useParentTitle: false` on the current route always opts out. Otherwise the
 * inherited source is:
 * - with `ui.useParentTitle: true` on the current route: the nearest non-overlay ancestor with
 *   a `ui.title`;
 * - else, when a non-overlay ancestor sets `ui.useTitleForChildren: true`: that flagged
 *   ancestor itself (the deepest one, when nested) — its own `title` is what children inherit.
 *
 * The subtitle is the current route's own (staticData first, then context); when the title was
 * inherited, the source route's `subtitle` is the final fallback so title and subtitle travel
 * together.
 */
export function resolvePageHeader(matches: readonly RouteMatchLike[]): PageHeaderResolution {
    const matchToUse = matches.findLast((item) => !isOverlayRoute(item.staticData));
    const lastMatch = matches.at(-1);
    const ownUi = matchToUse?.staticData.ui;

    const inheritedSource =
        ownUi?.useParentTitle === false
            ? undefined
            : ownUi?.useParentTitle === true
              ? matches.findLast(
                    (item) => !isOverlayRoute(item.staticData) && item !== matchToUse && item.staticData.ui?.title,
                )
              : matches.findLast(
                    (item) =>
                        !isOverlayRoute(item.staticData) &&
                        item !== matchToUse &&
                        item.staticData.ui?.useTitleForChildren === true,
                );

    const trail = resolveBreadcrumbTrail(matches);

    const ownTitle = ownUi?.title || matchToUse?.context?.ui?.title;
    // A route that hides its title hides it for its untitled descendants too: a record page's tab
    // children would otherwise pick up an ancestor section title and render a second H1.
    const hiddenByAncestor = matches.some(
        (item) => item !== matchToUse && !isOverlayRoute(item.staticData) && item.staticData.ui?.hideTitle === true,
    );
    const hideTitle = ownUi?.hideTitle === true || (!ownTitle && hiddenByAncestor);
    const inheritedTitle = ownTitle || hideTitle ? undefined : inheritedSource?.staticData.ui?.title;
    const fallbackTitle =
        ownTitle || inheritedTitle || hideTitle
            ? undefined
            : trail.length === 1
              ? trail.at(-1)?.breadcrumb.title || lastMatch?.staticData.breadcrumb?.title
              : undefined;

    const title = ownUi?.hideTitle === true ? undefined : ownTitle || inheritedTitle || fallbackTitle;

    const subtitle =
        matchToUse?.staticData.ui?.subtitle ||
        matchToUse?.context?.ui?.subtitle ||
        (inheritedTitle ? inheritedSource?.staticData.ui?.subtitle : undefined);

    return { title, subtitle, matchToUse };
}

/**
 * Resolves the browser-tab title (`document.title`) for a set of matches.
 *
 * The source is the deepest match with a title, skipping overlay matches unless the overlay is the
 * current (last) match — an overlay that names itself (e.g. an edit drawer) still wins, while an
 * overlay without one falls back to the page behind it instead of resetting the tab to bare
 * "Curium". A match's title is its `metadata.title`, else its page title (`ui.title`), so a page
 * that only sets the page title still names its tab (CUR-1341).
 *
 * When the source match's own params carry an `id`, it is appended: "Claim - 370 - Curium".
 */
export function resolveDocumentTitle(matches: readonly RouteMatchLike[]): string {
    const lastMatch = matches.at(-1);
    const titleOf = (item: RouteMatchLike) => item.staticData.metadata?.title || item.staticData.ui?.title;
    const source = matches.findLast(
        (item) => Boolean(titleOf(item)) && (!isOverlayRoute(item.staticData) || item === lastMatch),
    );

    const title = source ? titleOf(source) : undefined;
    if (!source || !title) {
        return 'Curium';
    }

    const id = source.params?.id;
    return `${title}${id !== undefined && id !== null ? ` - ${id}` : ''} - Curium`;
}

/**
 * Resolves the page icon from matches: the deepest non-overlay match with a `ui.icon`.
 * Index routes and tab children typically carry no icon, so this walks up to the nearest
 * ancestor that does.
 */
export function resolveMatchIcon(matches: readonly RouteMatchLike[]): LucideIcon | undefined {
    return matches.findLast((item) => !isOverlayRoute(item.staticData) && item.staticData.ui?.icon)?.staticData.ui
        ?.icon;
}

/**
 * Structural subset of a route-tree node (from `router.routesByPath`) used for icon resolution.
 */
export type RouteTreeNodeLike = {
    options?: { staticData?: StaticDataRouteOption };
    parentRoute?: RouteTreeNodeLike;
};

/**
 * Resolves a route's icon from the route *tree* (as opposed to the current matches): the
 * route's own `ui.icon`, else the nearest parent's. Used by menus/landing pages that show
 * icons for routes other than the current one.
 */
export function resolveRouteIcon(route: RouteTreeNodeLike | undefined): LucideIcon | undefined {
    for (let current = route; current; current = current.parentRoute) {
        const icon = current.options?.staticData?.ui?.icon;
        if (icon) {
            return icon;
        }
    }

    return undefined;
}
